import { ethers, getBigInt } from 'ethers';
import { mileStoneDuration } from '../config/data';
import { formatNumber } from './amountFormat';

const toWeiBigInt = (value) => getBigInt(value ?? 0);
const BPS_DENOM = toWeiBigInt(10000);
const asBigInt = toWeiBigInt;
const ZERO_WEI = toWeiBigInt(0);

/** Gross locked payment → fee + net escrow (milestone amounts must sum to net). */
export function splitLockedEscrowFee(grossWei, feeBps = 0) {
    const gross = asBigInt(grossWei);
    const bps = asBigInt(feeBps);
    const fee = (gross * bps) / BPS_DENOM;
    const netEscrow = gross - fee;
    return { feeWei: fee, netEscrowWei: netEscrow };
}

/**
 * Build on-chain custom schedule arrays from equal split template.
 * Unlock i at now + interval * (i + 1); amounts sum to net escrow.
 */
export function buildEqualCustomSchedulePayload({
    grossAmount,
    milestoneCount,
    intervalSeconds,
    feeBps = 0,
    decimals = 6,
    nowSec = Math.floor(Date.now() / 1000),
}) {
    const count = Math.floor(Number(milestoneCount));
    const interval = Number(intervalSeconds);
    const grossHuman = Number(grossAmount);
    if (!count || count < 1 || !interval || interval < 1 || !grossHuman || grossHuman <= 0) {
        return null;
    }

    const grossWei = ethers.parseUnits(String(grossAmount), decimals);
    const { netEscrowWei } = splitLockedEscrowFee(grossWei, feeBps);
    if (netEscrowWei <= ZERO_WEI) return null;

    const n = asBigInt(count);
    const per = netEscrowWei / n;
    const remainder = netEscrowWei % n;
    const unlockTimesSec = [];
    const milestoneAmountsWei = [];

    const unlockTimesSecTemplate = buildUnlockTimesForCustomTemplate(
        count,
        interval,
        nowSec,
    );
    for (let i = 0; i < count; i += 1) {
        unlockTimesSec.push(unlockTimesSecTemplate[i]);
        milestoneAmountsWei.push(i === count - 1 ? per + remainder : per);
    }

    return {
        unlockTimesSec,
        milestoneAmountsWei: milestoneAmountsWei.map((v) => v.toString()),
        netEscrowWei: netEscrowWei.toString(),
        feeWei: splitLockedEscrowFee(grossWei, feeBps).feeWei.toString(),
    };
}

/** Convert editor rows (human amounts + datetime) to chain payload. */
export function customRowsToSchedulePayload(rows, decimals = 6) {
    if (!rows?.length) return null;
    const unlockTimesSec = [];
    const milestoneAmountsWei = [];

    for (const row of rows) {
        const amount = Number(row.amount);
        if (!amount || amount <= 0) return null;
        const unlockMs = new Date(row.unlockAt).getTime();
        if (!Number.isFinite(unlockMs)) return null;
        unlockTimesSec.push(Math.floor(unlockMs / 1000));
        milestoneAmountsWei.push(ethers.parseUnits(String(amount), decimals).toString());
    }

    return { unlockTimesSec, milestoneAmountsWei };
}

export function schedulePayloadToRows({ unlockTimesSec, milestoneAmountsWei, decimals = 6 }) {
    if (!unlockTimesSec?.length || unlockTimesSec.length !== milestoneAmountsWei?.length) {
        return [];
    }
    return unlockTimesSec.map((unlockSec, index) => ({
        id: index + 1,
        unlockAt: toDatetimeLocalValue(unlockSec * 1000),
        amount: ethers.formatUnits(milestoneAmountsWei[index], decimals),
    }));
}

export function toDatetimeLocalValue(ms) {
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** One starter row for fully manual custom schedules (no equal-split template). */
export function buildInitialCustomScheduleRows({
    grossAmount,
    feeBps = 0,
    decimals = 6,
    unlockOffsetSec = 3600,
    nowMs = Date.now(),
}) {
    const grossHuman = Number(grossAmount);
    if (!grossHuman || grossHuman <= 0) return [];

    try {
        const grossWei = ethers.parseUnits(String(grossAmount), decimals);
        const { netEscrowWei } = splitLockedEscrowFee(grossWei, feeBps);
        if (netEscrowWei <= ZERO_WEI) return [];

        const unlockMs = nowMs + Number(unlockOffsetSec || 3600) * 1000;
        return [
            {
                id: Date.now(),
                unlockAt: toDatetimeLocalValue(unlockMs),
                amount: ethers.formatUnits(netEscrowWei, decimals),
            },
        ];
    } catch (_e) {
        return [];
    }
}

export function formatIntervalSeconds(seconds) {
    const s = Number(seconds);
    if (!s || s < 1) return '—';
    const match = mileStoneDuration.find((d) => d.timestamp === s);
    if (match) return match.name;

    if (s % 31536000 === 0) return `${s / 31536000} year${s / 31536000 > 1 ? 's' : ''}`;
    if (s % 2592000 === 0) return `${s / 2592000} month${s / 2592000 > 1 ? 's' : ''}`;
    if (s % 604800 === 0) return `${s / 604800} week${s / 604800 > 1 ? 's' : ''}`;
    if (s % 86400 === 0) return `${s / 86400} day${s / 86400 > 1 ? 's' : ''}`;
    if (s % 3600 === 0) return `${s / 3600} hour${s / 3600 > 1 ? 's' : ''}`;
    if (s % 60 === 0) return `${s / 60} min${s / 60 > 1 ? 's' : ''}`;
    return `${s} sec`;
}

export function formatDurationSpan(totalSeconds) {
    const s = Number(totalSeconds);
    if (!s || s < 1) return '—';

    const parts = [];
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const mins = Math.floor((s % 3600) / 60);

    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 && days === 0) parts.push(`${mins}m`);
    if (parts.length === 0) parts.push(`${s}s`);
    return parts.join(' ');
}

export function formatMilestoneReleaseDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

const startOfLocalDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Human label for when a milestone unlocks relative to now. */
export function formatRelativeUnlockLabel(
    unlockDate,
    referenceNow = new Date(),
) {
    if (!(unlockDate instanceof Date) || Number.isNaN(unlockDate.getTime())) {
        return '—';
    }

    const nowMs = referenceNow.getTime();
    const unlockMs = unlockDate.getTime();
    const diffSec = Math.max(0, Math.floor((unlockMs - nowMs) / 1000));

    const timePart = unlockDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    });

    const today = startOfLocalDay(referenceNow);
    const unlockDay = startOfLocalDay(unlockDate);
    const dayDiff = Math.round(
        (unlockDay.getTime() - today.getTime()) / 86400000,
    );

    if (dayDiff === 0) {
        if (diffSec < 60) return 'In about 1 minute';
        if (diffSec < 3600) {
            const mins = Math.max(1, Math.round(diffSec / 60));
            return mins === 1 ? 'In about 1 minute' : `In about ${mins} minutes`;
        }
        const hours = Math.max(1, Math.round(diffSec / 3600));
        return hours === 1 ? 'In about 1 hour' : `In about ${hours} hours`;
    }
    if (dayDiff === 1) return `Tomorrow · ${timePart}`;
    if (dayDiff === 2) return `Day after tomorrow · ${timePart}`;
    if (dayDiff > 2 && dayDiff < 7) {
        const weekday = unlockDate.toLocaleDateString(undefined, {
            weekday: 'long',
        });
        return `${weekday} · ${timePart}`;
    }
    return formatMilestoneReleaseDate(unlockDate);
}

function calendarUnlockAtDayOffset(baseNowSec, dayOffset, hours, minutes) {
    const d = new Date(baseNowSec * 1000);
    d.setDate(d.getDate() + dayOffset);
    d.setHours(hours, minutes, 0, 0);
    return Math.floor(d.getTime() / 1000);
}

/**
 * Custom schedule template: first unlock soon (~1h), then calendar days.
 * Day-or-longer intervals use equal spacing (e.g. daily, weekly).
 */
function buildUnlockTimesForCustomTemplate(count, intervalSeconds, nowSec) {
    const interval = Number(intervalSeconds);
    const times = [];

    if (interval >= 86400) {
        for (let i = 0; i < count; i += 1) {
            times.push(nowSec + interval * (i + 1));
        }
        return times;
    }

    const firstUnlockSec = nowSec + Math.max(interval, 3600);
    times.push(firstUnlockSec);
    const firstDate = new Date(firstUnlockSec * 1000);
    const hours = firstDate.getHours();
    const minutes = firstDate.getMinutes();

    for (let i = 1; i < count; i += 1) {
        let unlock = calendarUnlockAtDayOffset(nowSec, i, hours, minutes);
        if (unlock <= times[i - 1]) {
            unlock = times[i - 1] + 86400;
        }
        times.push(unlock);
    }
    return times;
}

function formatTokenAmount(value) {
    return formatNumber(value);
}

/**
 * Preview schedule matching on-chain locked payment rules:
 * - Equal split: totalAmount / milestones (remainder on final claim)
 * - Milestone i unlocks at start + interval * i (seconds)
 */
export function buildMilestoneSchedule({
    totalAmount,
    milestoneCount,
    intervalSeconds,
    startDate = new Date(),
}) {
    const count = Math.floor(Number(milestoneCount));
    const interval = Number(intervalSeconds);
    const total = Number(totalAmount);

    if (!count || count < 1 || !interval || interval < 1 || !total || total <= 0) {
        return null;
    }

    const perMilestone = total / count;
    const remainder = total - perMilestone * count;
    const intervalLabel = formatIntervalSeconds(interval);
    const startSec = Math.floor(startDate.getTime() / 1000);
    const totalDurationSeconds = interval * count;
    const endDate = new Date((startSec + totalDurationSeconds) * 1000);

    const milestones = [];
    let cumulative = 0;

    for (let i = 1; i <= count; i += 1) {
        const isLast = i === count;
        const amount = isLast ? perMilestone + remainder : perMilestone;
        cumulative += amount;
        const unlockSec = startSec + interval * i;
        const unlockDate = new Date(unlockSec * 1000);
        const elapsedLabel = formatRelativeUnlockLabel(unlockDate, startDate);

        milestones.push({
            index: i,
            label: `Milestone ${i}`,
            amount,
            amountFormatted: formatTokenAmount(amount),
            cumulativeAmount: cumulative,
            cumulativeFormatted: formatTokenAmount(cumulative),
            percent: (amount / total) * 100,
            unlockDate,
            unlockDateLabel: formatMilestoneReleaseDate(unlockDate),
            elapsedLabel,
            isLast,
        });
    }

    return {
        totalAmount: total,
        totalFormatted: formatTokenAmount(total),
        milestoneCount: count,
        intervalSeconds: interval,
        intervalLabel,
        perMilestoneAmount: perMilestone,
        perMilestoneFormatted: formatTokenAmount(perMilestone),
        hasRemainder: Math.abs(remainder) > 1e-9,
        remainderFormatted: formatTokenAmount(remainder),
        totalDurationSeconds,
        totalDurationLabel: formatDurationSpan(totalDurationSeconds),
        endDate,
        endDateLabel: formatMilestoneReleaseDate(endDate),
        milestones,
    };
}

/**
 * Validate a custom schedule before calling initiateLockedPaymentWithSchedule.
 * @param {bigint|number} netEscrowAmount - sum of milestone amounts must equal this (after lock fee).
 */
export function validateCustomMilestoneSchedule({
    unlockTimesSec,
    milestoneAmounts,
    milestoneAmountsWei,
    netEscrowAmount,
    netEscrowWei,
    nowSec = Math.floor(Date.now() / 1000),
    maxMilestones = 52,
    maxDurationSec = 10 * 365 * 24 * 60 * 60,
}) {
    const times = unlockTimesSec?.map((t) => Number(t)) ?? [];
    const amountsWei =
        milestoneAmountsWei?.map((a) => asBigInt(a)) ??
        milestoneAmounts?.map((a) => asBigInt(Math.trunc(Number(a)))) ??
        [];
    const expectedWei =
        netEscrowWei != null
            ? asBigInt(netEscrowWei)
            : asBigInt(Math.trunc(Number(netEscrowAmount || 0)));

    if (!times.length || times.length !== amountsWei.length) {
        return { ok: false, error: 'Schedule length mismatch' };
    }
    if (times.length > maxMilestones) {
        return { ok: false, error: `Max ${maxMilestones} milestones` };
    }
    if (times[0] <= nowSec) {
        return { ok: false, error: 'First unlock must be in the future' };
    }
    const duration = times[times.length - 1] - times[0];
    if (duration > maxDurationSec) {
        return { ok: false, error: 'Schedule span exceeds 10 years' };
    }

    let sum = ZERO_WEI;
    for (let i = 0; i < times.length; i += 1) {
        if (amountsWei[i] <= ZERO_WEI) {
            return { ok: false, error: `Milestone ${i + 1} amount must be > 0` };
        }
        if (i > 0 && times[i] <= times[i - 1]) {
            return {
                ok: false,
                error: `Unlock times must be strictly increasing (milestone ${i + 1})`,
            };
        }
        sum += amountsWei[i];
    }
    if (sum !== expectedWei) {
        return { ok: false, error: 'Amounts must sum to net escrow after fees' };
    }
    return { ok: true };
}

/**
 * Build preview rows for a custom on-chain schedule.
 */
export function buildCustomMilestoneSchedule({
    unlockTimesSec,
    milestoneAmounts,
    startDate = new Date(),
}) {
    const times = unlockTimesSec?.map((t) => Number(t)) ?? [];
    const amounts = milestoneAmounts?.map((a) => Number(a)) ?? [];
    if (!times.length || times.length !== amounts.length) return null;

    const total = amounts.reduce((a, b) => a + b, 0);
    const referenceNow =
        startDate instanceof Date ? startDate : new Date();
    let cumulative = 0;
    const milestones = times.map((unlockSec, i) => {
        const amount = amounts[i];
        cumulative += amount;
        const unlockDate = new Date(unlockSec * 1000);
        return {
            index: i + 1,
            label: `Milestone ${i + 1}`,
            amount,
            amountFormatted: formatTokenAmount(amount),
            cumulativeAmount: cumulative,
            cumulativeFormatted: formatTokenAmount(cumulative),
            percent: total > 0 ? (amount / total) * 100 : 0,
            unlockDate,
            unlockDateLabel: formatMilestoneReleaseDate(unlockDate),
            elapsedLabel: formatRelativeUnlockLabel(unlockDate, referenceNow),
            isLast: i === times.length - 1,
        };
    });

    return {
        totalAmount: total,
        totalFormatted: formatTokenAmount(total),
        milestoneCount: times.length,
        milestones,
        endDate: milestones[milestones.length - 1]?.unlockDate,
        endDateLabel: milestones[milestones.length - 1]?.unlockDateLabel,
        customSchedule: true,
    };
}

export function formatRelativeUnlock(index, intervalSeconds) {
    const i = Number(index);
    const interval = Number(intervalSeconds);
    if (!i || !interval) return '';
    return `T+${formatDurationSpan(interval * i)}`;
}

/** First schedule slot that unlocks after `nowSec`. */
export function findNextFutureUnlockSec(
    scheduleSlots = [],
    nowSec = Math.floor(Date.now() / 1000),
) {
    for (const slot of scheduleSlots) {
        const unlockAt = Number(slot?.unlockAt ?? slot?.unlockSec ?? 0);
        if (unlockAt > nowSec) return unlockAt;
    }
    return null;
}

/** First equal-interval milestone unlock strictly after `nowSec`. */
export function getNextFutureEqualUnlockSec(
    startTime,
    intervalSeconds,
    totalMilestones,
    nowSec = Math.floor(Date.now() / 1000),
) {
    const start = Number(startTime);
    const intv = Number(intervalSeconds);
    const total = Number(totalMilestones);
    if (!start || !intv || !total) return null;
    for (let n = 1; n <= total; n += 1) {
        const unlockAt = start + intv * n;
        if (unlockAt > nowSec) return unlockAt;
    }
    return null;
}

/** Primary + secondary labels for the receiver "Next release" column. */
export function buildReceiverNextReleaseMeta({
    isComplete,
    isDisputeRaised,
    claimableFloat,
    nextUnlockSec,
    scheduleSlots = [],
    startTime,
    intervalSeconds,
    totalMilestones,
    referenceNow = new Date(),
}) {
    const nowSec = Math.floor(referenceNow.getTime() / 1000);
    const isClaimableNow = !isDisputeRaised && claimableFloat > 0;

    if (isComplete) {
        return { primary: 'Complete', secondary: null, nextUnlockSec: null };
    }

    const futureFromSchedule = scheduleSlots.length
        ? findNextFutureUnlockSec(scheduleSlots, nowSec)
        : null;
    const futureFromEqual =
        startTime != null && intervalSeconds != null && totalMilestones != null
            ? getNextFutureEqualUnlockSec(
                  startTime,
                  intervalSeconds,
                  totalMilestones,
                  nowSec,
              )
            : null;
    const futureSec = futureFromSchedule ?? futureFromEqual;

    if (isClaimableNow) {
        if (futureSec) {
            const d = new Date(futureSec * 1000);
            return {
                primary: 'Available now',
                secondary: `Next unlock: ${formatRelativeUnlockLabel(d, referenceNow)} · ${formatMilestoneReleaseDate(d)}`,
                nextUnlockSec: futureSec,
            };
        }
        return { primary: 'Available now', secondary: null, nextUnlockSec: null };
    }

    const unlockSec =
        nextUnlockSec ??
        futureSec ??
        (scheduleSlots.length
            ? Number(
                  scheduleSlots[0]?.unlockAt ?? scheduleSlots[0]?.unlockSec ?? 0,
              ) || null
            : null);

    if (!unlockSec) {
        return { primary: '—', secondary: null, nextUnlockSec: null };
    }

    const d = new Date(unlockSec * 1000);
    return {
        primary: formatRelativeUnlockLabel(d, referenceNow),
        secondary: formatMilestoneReleaseDate(d),
        nextUnlockSec: unlockSec,
    };
}

export function buildCustomUpcomingMilestoneRows({
    scheduleSlots = [],
    claimedMilestones = 0,
    decimals = 6,
    referenceNow = new Date(),
}) {
    const nowSec = Math.floor(referenceNow.getTime() / 1000);
    const claimed = Number(claimedMilestones);
    return scheduleSlots.slice(claimed).map((slot, offset) => {
        const index = claimed + offset + 1;
        const unlockAt = Number(slot?.unlockAt ?? slot?.unlockSec ?? 0);
        const rawAmount =
            slot?.amount != null ? slot.amount : slot?.amountWei ?? 0;
        const amountFloat = parseFloat(
            ethers.formatUnits(String(rawAmount), decimals),
        );
        const d = new Date(unlockAt * 1000);
        return {
            index,
            amountFloat,
            unlockAt,
            relative: formatRelativeUnlockLabel(d, referenceNow),
            dateTime: formatMilestoneReleaseDate(d),
            isUnlocked: nowSec >= unlockAt,
            isNext: offset === 0,
        };
    });
}

export function buildEqualUpcomingMilestoneRows({
    startTime,
    intervalSeconds,
    totalMilestones,
    claimedMilestones,
    totalAmountWei,
    decimals = 6,
    referenceNow = new Date(),
}) {
    const nowSec = Math.floor(referenceNow.getTime() / 1000);
    const start = Number(startTime);
    const intv = Number(intervalSeconds);
    const total = Number(totalMilestones);
    const claimed = Number(claimedMilestones);
    if (!start || !intv || !total || claimed >= total) return [];

    const perMsWei = asBigInt(totalAmountWei) / asBigInt(total);
    const rows = [];
    for (let n = claimed + 1; n <= total; n += 1) {
        const unlockAt = start + intv * n;
        const d = new Date(unlockAt * 1000);
        rows.push({
            index: n,
            amountFloat: parseFloat(
                ethers.formatUnits(perMsWei.toString(), decimals),
            ),
            unlockAt,
            relative: formatRelativeUnlockLabel(d, referenceNow),
            dateTime: formatMilestoneReleaseDate(d),
            isUnlocked: nowSec >= unlockAt,
            isNext: rows.length === 0,
        });
    }
    return rows;
}
